import { useState, useEffect, useMemo, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TaskItemsForm } from "./task-items-form";
import { TaskItem } from "./task-items-form";
import { apiRequest } from "@/lib/queryClient";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Star, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { sk } from "date-fns/locale";
import { formatCurrency } from "@/lib/currency";
import { useAuth } from "@/hooks/use-auth";
// import { calculateTaskAmount, validateTaskInput, convertTaskForBackend, convertTaskItemsForBackend } from "@shared/task-calculations";

const taskFormSchema = z.object({
  title: z.string().min(1, "Názov úlohy je povinný"),
  description: z.string().optional(),
  clientId: z.string().min(1, "Klient je povinný"),
  assignedToId: z.string().optional(),
  hoursWorked: z.string().optional().default(""), // Optional - only required for standard tasks
  status: z.enum(["pending", "in_progress", "completed"], {
    errorMap: () => ({ message: "Stav je povinný" })
  }),
  dueDate: z.date({
    errorMap: () => ({ message: "Neplatný dátum" })
  }).optional(),
  acceptDeadline: z.date({
    errorMap: () => ({ message: "Neplatný dátum" })
  }).optional(),
  isStarred: z.boolean().default(false),
  type: z.enum(["standard", "cash", "offset", "bonus"], {
    errorMap: () => ({ message: "Typ úlohy je povinný" })
  }).default("standard"),
  billable: z.boolean().default(false),
  manualAmount: z.union([
    z.string(),
    z.number(),
  ]).optional().transform(val => val === "" ? null : val),
  quantity: z.union([
    z.string().transform(val => {
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Neplatné číslo");
      return num;
    }),
    z.number(),
  ]).default("1"),
  unitPrice: z.union([
    z.string().transform(val => {
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Neplatné číslo");
      return num;
    }),
    z.number(),
  ]).default("0"),
});

export interface Client {
  id: number;
  name: string;
  businessName?: string;
  hourlyRate: string; // Zmena na string pre kompatibilitu s existujúcimi dátami
}

export interface TeamMember {
  id: number;
  invitedEmail: string;
  memberName?: string;
  firstName?: string;
  lastName?: string;
  role: string;
  status: string;
}

interface TaskFormValues {
  title: string;
  description?: string;
  clientId: string;
  assignedToId?: string;
  hoursWorked: string;
  status: "pending" | "in_progress" | "completed";
  dueDate?: Date;
  acceptDeadline?: Date;
  isStarred: boolean;
  type: "standard" | "cash" | "offset" | "bonus";
  billable: boolean;
  manualAmount?: string | number | null;
  quantity: number;
  unitPrice: number;
}

interface EnhancedTaskFormProps {
  defaultValues?: TaskFormValues & {
    subtotal_cents?: number;
    tax_cents?: number;
    total_cents?: number;
  };
  clients: Client[];
  teamMembers?: TeamMember[];
  onSubmit: (data: TaskFormValues, items: TaskItem[]) => void;
  isSubmitting?: boolean;
  taskId?: number; // Pre editáciu existujúcej úlohy
  onSuccess?: () => void; // Callback po úspešnom uložení
  onNavigateToCreateClient?: () => void; // Keď nie sú žiadni klienti – odkaz na vytvorenie
  onClientCreated?: (client: { id: number }) => void; // Po rýchlom vytvorení klienta – refetch
}

export function EnhancedTaskForm({
  defaultValues,
  clients,
  teamMembers = [],
  onSubmit,
  isSubmitting = false,
  taskId,
  onSuccess,
  onNavigateToCreateClient,
  onClientCreated,
}: EnhancedTaskFormProps) {
  console.log('[FRONTEND] EnhancedTaskForm component created/re-rendered with taskId:', taskId);
  const { t } = useTranslation(['tasks']);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // 1) Najprv odvodíme defaultValues (bez použitia `form`)
  const formDefaultValues: TaskFormValues = {
    title: "",
    description: "",
    clientId: "",
    assignedToId: "",
    hoursWorked: "",
    status: "pending",
    dueDate: new Date(), // Default dátum je dnes
    isStarred: false,
    type: "standard",
    billable: false,
    manualAmount: "",
    quantity: 1,
    unitPrice: 0,
    ...defaultValues,
  };

  // 2) Vytvoríme form ako PRVÉ v komponente
  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: formDefaultValues,
  });

  // 3) State po useForm
  const [showManualAmount, setShowManualAmount] = useState(
    defaultValues?.type === "cash" || 
    defaultValues?.type === "bonus" ||
    defaultValues?.type === "offset"
  );
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(
    defaultValues?.clientId
  );
  // Načítanie položiek cez React Query ak máme taskId
  const { data: apiItems = [] } = useQuery<TaskItem[]>({
    queryKey: ['/api/tasks', taskId, 'items'],
    queryFn: async () => {
      if (!taskId) return [];
      const res = await apiRequest('GET', `/api/tasks/${taskId}/items`);
      return res.json();
    },
    enabled: !!taskId,
    refetchInterval: 5000, // Refetch každých 5 sekúnd namiesto každej sekundy
  });
  
  // Jednoduchý lokálny state pre položky (len pre nové úlohy)
  const [localItems, setLocalItems] = useState<TaskItem[]>([]);
  
  // Použijeme API položky ak máme taskId, inak lokálne položky
  const items = taskId ? apiItems : localItems;
  
  // Debug log pre zmeny items state - odstránené kvôli nekonečnej slučke

  // Položky sa načítajú automaticky cez React Query

  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [acceptDeadlineOpen, setAcceptDeadlineOpen] = useState(false);
  const [quickCreateClientOpen, setQuickCreateClientOpen] = useState(false);
  const [quickCreateData, setQuickCreateData] = useState({ name: "", tel: "", email: "", hourlyRate: "" });
  const [quickCreateSubmitting, setQuickCreateSubmitting] = useState(false);

  // Načítanie existujúcich údajov úlohy a položiek pri editácii
  useEffect(() => {
    if (taskId) {
      const loadTaskData = async () => {
        try {
          // Načítame údaje úlohy
          const taskResponse = await fetch(`/api/tasks/${taskId}`, {
            credentials: 'include'
          });
          if (taskResponse.ok) {
            const taskData = await taskResponse.json();
            
            // Nastavíme údaje do formulára
            // Default dueDate je createdAt ak nie je nastavené
            const defaultDueDate = taskData.dueDate 
              ? new Date(taskData.dueDate) 
              : taskData.createdAt 
                ? new Date(taskData.createdAt) 
                : new Date();
                
            // Konvertovať assigned_to_id na assignedToId
            const assignedTo = taskData.assignedToId || taskData.assigned_to_id || taskData.assignedTo;
            console.log('[TASK FORM] Loading task data:', { 
              taskId, 
              assignedToId: taskData.assignedToId, 
              assigned_to_id: taskData.assigned_to_id,
              resolved: assignedTo
            });
            
            form.reset({
              title: taskData.title || "",
              description: taskData.description || "",
              clientId: (taskData.clientId || taskData.client_id) ? (taskData.clientId || taskData.client_id).toString() : "",
              assignedToId: assignedTo ? assignedTo.toString() : "",
              hoursWorked: (taskData.hoursWorked || taskData.actual_hours || taskData.hours_worked)?.toString() || "0",
              status: taskData.status || "pending",
              dueDate: defaultDueDate,
              acceptDeadline: (taskData.acceptDeadline ?? taskData.accept_deadline) ? new Date(taskData.acceptDeadline ?? taskData.accept_deadline) : undefined,
              isStarred: taskData.isStarred || false,
              type: taskData.type || "standard",
              billable: taskData.billable || false,
              manualAmount: taskData.manualAmount?.toString() || "",
              quantity: 1,
              unitPrice: 0,
              // Pridáme finančné súčty z API
              subtotal_cents: taskData.subtotal_cents,
              tax_cents: taskData.tax_cents,
              total_cents: taskData.total_cents,
            } as any);
            
            // Nastavíme vybraného klienta
            const clientId = taskData.clientId || taskData.client_id;
            if (clientId) {
              setSelectedClientId(clientId.toString());
            }
          }

                // Položky sa načítajú automaticky cez React Query
        } catch (error) {
          console.error('Error loading task data for edit:', error);
        }
      };
      loadTaskData();
    }
  }, [taskId, form]);

  // Polling už nepotrebujeme - každá položka sa pridáva/mazá individuálne

  // useForm hook presunutý pred useEffect

  // Sledujeme zmeny v type úlohy - presunuté do useEffect
  const [taskType, setTaskType] = useState<string>("standard");
  
  // Automaticky nastavíme billable podľa typu úlohy
  useEffect(() => {
    const currentTaskType = form.watch("type");
    setTaskType(currentTaskType);
    
    if (currentTaskType === "cash") {
      form.setValue("billable", true);
    } else if (currentTaskType === "offset") {
      form.setValue("billable", false);
    }
  }, [form]);

  // Sledujeme zmeny v type úlohy a nastavujeme showManualAmount
  useEffect(() => {
    const shouldShowManualAmount = taskType === "cash" || taskType === "bonus" || taskType === "offset";
    if (shouldShowManualAmount !== showManualAmount) {
      setShowManualAmount(shouldShowManualAmount);
    }
  }, [taskType]);

  // Získame vybraného klienta
  const selectedClient = Array.isArray(clients) ? clients.find(
    (client) => client.id.toString() === selectedClientId
  ) : undefined;

  // Use API calculated totals if available, otherwise fallback to items calculation
  const itemsTotal = defaultValues?.subtotal_cents !== undefined ? (defaultValues.subtotal_cents / 100) : items.reduce((sum, item) => sum + item.amount, 0);

  // 3) Čistá utilita – žiadny `form` vo vnútri!
  function calculateTotals(values: TaskFormValues, items: TaskItem[], selectedClient: Client | undefined, isVatPayer: boolean, companyVatRate?: string) {
    const taskType = values.type ?? "standard";
    const hoursWorked = parseFloat(values.hoursWorked || "0");
    const manualAmount = values.manualAmount ? Number(values.manualAmount) : 0;
    const hourlyRate = selectedClient ? parseFloat(String(selectedClient.hourlyRate || 0)) : 0;
    
    console.log('[calculateTotals]', { hoursWorked, hourlyRate, selectedClient, hoursWorkedType: typeof hoursWorked, hourlyRateType: typeof hourlyRate, isVatPayer, companyVatRate });
    
    // Použijeme jednotnú logiku výpočtov
    
    const baseAmount = hoursWorked * hourlyRate;
    console.log('[calculateTotals] baseAmount:', baseAmount, 'from hoursWorked:', hoursWorked, 'and hourlyRate:', hourlyRate, 'isVatPayer:', isVatPayer);
    // Use API calculated totals if available, otherwise fallback to items calculation
    // For existing tasks with API totals, only sum up the actual items, not the whole subtotal
    const itemsNet = items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const vatRateDecimal = (companyVatRate ? parseFloat(companyVatRate) : 20) / 100;
    
    // If we have API totals for existing tasks, use them directly
    let totalNet, totalVat, totalWithVat;
    if (taskId && defaultValues?.total_cents !== undefined) {
      totalNet = defaultValues.subtotal_cents / 100;
      totalVat = defaultValues.tax_cents / 100;
      // If user is not VAT payer, use subtotal (without VAT), otherwise use total (with VAT)
      totalWithVat = isVatPayer ? (defaultValues.total_cents / 100) : (defaultValues.subtotal_cents / 100);
      console.log('[calculateTotals] Using API totals - subtotal_cents:', defaultValues.subtotal_cents, 'total_cents:', defaultValues.total_cents, 'isVatPayer:', isVatPayer, 'totalWithVat:', totalWithVat);
    } else {
      // For new tasks, calculate totals
      totalNet = baseAmount + itemsNet + (manualAmount || 0);
      totalVat = isVatPayer ? (totalNet * vatRateDecimal) : 0;
      totalWithVat = isVatPayer ? (totalNet + totalVat) : totalNet;
      console.log('[calculateTotals] Calculated - totalNet:', totalNet, 'totalVat:', totalVat, 'totalWithVat:', totalWithVat, 'isVatPayer:', isVatPayer, 'vatRateDecimal:', vatRateDecimal);
    }
    
    return {
      baseNet: baseAmount,
      baseVat: isVatPayer ? (baseAmount * vatRateDecimal) : 0,
      itemsNet: itemsNet,
      itemsVat: isVatPayer ? (itemsNet * vatRateDecimal) : 0,
      totalNet: totalNet,
      totalVat: totalVat,
      totalGross: totalWithVat,
      itemsTotal: itemsNet, // For backward compatibility
      hoursTotal: isVatPayer ? (baseAmount + baseAmount * vatRateDecimal) : baseAmount,
      isVatPayer: isVatPayer // Pridáme isVatPayer do totals objektu
    };
  }

  // 4) Watch/derive hodnoty až po useForm
  const watched = form.watch();
  const totals = useMemo(
    () => {
      const companyVatRate = user?.companyVatRate ? String(user.companyVatRate) : "20";
      // Explicitne skontrolujeme, či je isVatPayer false alebo undefined/null
      // Ak je undefined/null, považujeme to za false (nie je platca DPH)
      const isVatPayer = user?.isVatPayer === true;
      console.log('[EnhancedTaskForm] User VAT status:', { 
        isVatPayer: isVatPayer, 
        userIsVatPayer: user?.isVatPayer,
        userIsVatPayerType: typeof user?.isVatPayer,
        companyVatRate: companyVatRate,
        user: user ? { 
          id: user.id, 
          email: user.email, 
          isVatPayer: user.isVatPayer,
          companyVatRate: user.companyVatRate,
          // Show all user properties that might be related to VAT
          allVatProps: {
            isVatPayer: (user as any).isVatPayer,
            is_vat_payer: (user as any).is_vat_payer,
            companyVatRate: (user as any).companyVatRate,
            company_vat_rate: (user as any).company_vat_rate
          }
        } : null
      });
      const result = calculateTotals(watched as TaskFormValues, items, selectedClient, isVatPayer, companyVatRate);
      console.log('[EnhancedTaskForm] Calculated totals:', result, 'watched:', watched, 'selectedClient:', selectedClient, 'companyVatRate:', companyVatRate, 'isVatPayer:', isVatPayer);
      console.log('[EnhancedTaskForm] Full user object:', JSON.stringify(user, null, 2));
      return result;
    },
    [watched, items, selectedClient, user]
  );
  const { totalNet, totalVat, totalGross, hoursTotal, isVatPayer } = totals;
  const total = totalGross; // For backward compatibility

  // formatCurrency now imported from centralized utility

  // Sledujeme zmeny v klientovi
  const clientId = form.watch("clientId");
  useEffect(() => {
    if (clientId !== selectedClientId) {
      setSelectedClientId(clientId);
    }
  }, [clientId, selectedClientId]);

  // Spracovanie formulára
  const handleSubmit = (values: TaskFormValues) => {
    // Basic validation - check required fields
    if (!values.title || values.title.trim() === '') {
      form.setError("title", { message: "Názov úlohy je povinný" });
      return;
    }
    
    if (!values.clientId || values.clientId === '' || values.clientId === 'undefined' || values.clientId === 'none') {
      const msg = (!clients || clients.length === 0)
        ? (t("tasks:createClientFirst") || "Vytvor najprv klienta")
        : (t("tasks:clientRequired") || "Klient je povinný");
      form.setError("clientId", { message: msg });
      return;
    }
    
    // For standard tasks, check if hours are provided (0 is valid)
    if (values.type === 'standard' && (values.hoursWorked === '' || values.hoursWorked === undefined || values.hoursWorked === null)) {
      form.setError("hoursWorked", { message: "Pre štandardné úlohy sú hodiny povinné" });
      return;
    }
    
    // For cash/bonus tasks, check if manual amount is provided
    if ((values.type === 'cash' || values.type === 'bonus') && (!values.manualAmount || Number(values.manualAmount) <= 0)) {
      form.setError("manualAmount", { message: "Pre hotovosť/bonus je manuálna suma povinná" });
      return;
    }
    
    // For offset tasks, check if hours or manual amount is provided
    if (values.type === 'offset' && (values.hoursWorked === '' || values.hoursWorked === undefined || values.hoursWorked === null) && (!values.manualAmount || Number(values.manualAmount) === 0)) {
      form.setError("hoursWorked", { message: "Pre zápočet sú potrebné hodiny alebo manuálna suma" });
      return;
    }
    
    // Spracujeme assignedToId - ak je "none", nastavíme na undefined
    const processedValues = {
      ...values,
      assignedToId: values.assignedToId === "none" ? undefined : values.assignedToId,
      items: items,
      totals: {
        baseNet: totals.baseNet,
        baseVat: totals.baseVat,
        itemsNet: totals.itemsNet,
        itemsVat: totals.itemsVat,
        totalNet: totals.totalNet,
        totalVat: totals.totalVat,
        totalGross: totals.totalGross,
      }
    };
    
        // Konvertujeme na backend formát (bez položiek - tie sa ukladajú individuálne cez React Query)
        const parsedClientId = processedValues.clientId ? parseInt(String(processedValues.clientId)) : NaN;
        const backendData = {
          title: processedValues.title,
          description: processedValues.description,
          client_id: !isNaN(parsedClientId) ? parsedClientId : null,
          hours_worked: processedValues.hoursWorked,
          is_starred: processedValues.isStarred,
          quantity: processedValues.quantity,
          unit_price: processedValues.unitPrice,
          assigned_to_id: (() => {
            const aid = processedValues.assignedToId;
            if (!aid || aid === "none") return null;
            const n = parseInt(String(aid));
            return isNaN(n) ? null : n;
          })(),
          actual_hours: processedValues.hoursWorked ? parseFloat(String(processedValues.hoursWorked)) : null,
          hourly_rate: selectedClient && !isNaN(parseFloat(String(selectedClient.hourlyRate ?? ""))) ? parseFloat(String(selectedClient.hourlyRate)) : null,
          status: processedValues.status,
          due_date: processedValues.dueDate instanceof Date ? processedValues.dueDate.toISOString() : processedValues.dueDate,
          accept_deadline: processedValues.acceptDeadline instanceof Date ? processedValues.acceptDeadline.toISOString() : processedValues.acceptDeadline,
          type: processedValues.type,
          task_type: processedValues.type,
          billable: processedValues.billable,
          manual_amount: processedValues.manualAmount ? parseFloat(processedValues.manualAmount.toString()) : null
          // items sa už neposielajú - ukladajú sa individuálne cez React Query mutations
        };
    
    onSubmit(backendData, items);
  };


  // Memoizovaný prázdny callback pre TaskItemsForm
  const handleItemsChange = useCallback((updatedItems: TaskItem[]) => {
    if (!taskId) {
      setLocalItems(updatedItems);
    }
  }, [taskId]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("tasks:form.title")}</FormLabel>
              <FormControl>
                <Input placeholder={t("tasks:form.titlePlaceholder")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("tasks:form.description")}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t("tasks:form.descriptionPlaceholder")}
                  className="resize-none"
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="clientId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("tasks:form.client")}</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    setSelectedClientId(value);
                  }}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("tasks:selectClient")}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Array.isArray(clients) && clients.length > 0 ? clients.map((client) => (
                      <SelectItem key={client.id} value={client.id.toString()}>
                        {client.businessName || client.name}
                      </SelectItem>
                    )) : (
                      <SelectItem value="__no_clients__" disabled>
                        {t("tasks:createClientFirst") || "Vytvor najprv klienta"}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <div className="flex gap-2 mt-1">
                  <Popover open={quickCreateClientOpen} onOpenChange={setQuickCreateClientOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-primary border-primary/30"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {t("tasks:goCreateClient") || "Vytvoriť klienta"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-96 sm:w-[420px] p-4 z-[200]" align="start" sideOffset={8}>
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-semibold text-sm">Rýchle vytvorenie klienta</h4>
                          <p className="text-xs text-muted-foreground mt-0.5">Povinné: názov firmy</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium">Názov firmy / meno <span className="text-destructive">*</span></label>
                          <Input
                            placeholder="napr. Firma s.r.o."
                            value={quickCreateData.name}
                            onChange={(e) => setQuickCreateData((p) => ({ ...p, name: e.target.value }))}
                            className="mt-1"
                            autoComplete="off"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Telefón</label>
                          <Input
                            type="tel"
                            placeholder="+421 900 123 456"
                            value={quickCreateData.tel}
                            onChange={(e) => setQuickCreateData((p) => ({ ...p, tel: e.target.value }))}
                            className="mt-1"
                            autoComplete="off"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Email</label>
                          <Input
                            type="email"
                            placeholder="klient@firma.sk"
                            value={quickCreateData.email}
                            onChange={(e) => setQuickCreateData((p) => ({ ...p, email: e.target.value }))}
                            className="mt-1"
                            autoComplete="off"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Hodinovka (€)</label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="napr. 25"
                            value={quickCreateData.hourlyRate}
                            onChange={(e) => setQuickCreateData((p) => ({ ...p, hourlyRate: e.target.value }))}
                            className="mt-1"
                            autoComplete="off"
                          />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                          <Button type="button" variant="outline" size="sm" onClick={() => setQuickCreateClientOpen(false)}>
                            Zrušiť
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            disabled={!quickCreateData.name.trim() || quickCreateSubmitting}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!quickCreateData.name.trim()) return;
                              setQuickCreateSubmitting(true);
                              try {
                                const hourlyRate = quickCreateData.hourlyRate.trim()
                                  ? parseFloat(quickCreateData.hourlyRate.replace(",", "."))
                                  : undefined;
                                const res = await apiRequest("POST", "/api/clients", {
                                  name: quickCreateData.name.trim(),
                                  businessName: quickCreateData.name.trim(),
                                  phone: quickCreateData.tel.trim() || undefined,
                                  email: quickCreateData.email.trim() || undefined,
                                  hourlyRate: !isNaN(hourlyRate) && hourlyRate != null ? hourlyRate : undefined,
                                });
                                const created = await res.json();
                                await queryClient.refetchQueries({ queryKey: ["/api/clients"] });
                                const newId = created?.id;
                                if (newId != null) {
                                  form.setValue("clientId", newId.toString());
                                  setSelectedClientId(newId.toString());
                                  onClientCreated?.({ id: newId });
                                }
                                setQuickCreateClientOpen(false);
                                setQuickCreateData({ name: "", tel: "", email: "", hourlyRate: "" });
                              } catch (err) {
                                console.error("Quick create client error:", err);
                              } finally {
                                setQuickCreateSubmitting(false);
                              }
                            }}
                          >
                            {quickCreateSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Vytvoriť"}
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {(!clients || clients.length === 0) && onNavigateToCreateClient && (
                    <Button
                      type="button"
                      variant="link"
                      className="h-8 p-0 text-primary"
                      onClick={onNavigateToCreateClient}
                    >
                      {t("tasks:goCreateClient") || "Vytvoriť klienta"} →
                    </Button>
                  )}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="assignedToId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("tasks:form.assignedTo")}</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("Vyberte zamestnanca")}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">
                      {t("Nie je pridelené")}
                    </SelectItem>
                    {Array.isArray(teamMembers) && teamMembers.length > 0 ? teamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id.toString()}>
                        {member.memberName || member.invitedEmail} ({member.role})
                      </SelectItem>
                    )) : null}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Hodinovka - zobrazí sa len ak je vybraný klient */}
        {selectedClient && (
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
              <span className="text-sm font-medium">💰 Hodinovka klienta:</span>
              <span className="text-sm">{formatCurrency(parseFloat(selectedClient.hourlyRate) || 0)}</span>
            </div>
            <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
              Táto hodinovka sa použije na výpočet sumy úlohy
            </p>
          </div>
        )}

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("tasks:form.type")}</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  setShowManualAmount(
                    value === "cash" || value === "offset" || value === "bonus"
                  );
                }}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                                      <SelectValue
                    placeholder={t("tasks:form.typePlaceholder")}
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="standard">{t("tasks:types.standard")}</SelectItem>
                <SelectItem value="cash">{t("tasks:types.cash")}</SelectItem>
                <SelectItem value="offset">{t("tasks:types.offset")}</SelectItem>
                <SelectItem value="bonus">{t("tasks:types.bonus")}</SelectItem>
              </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Hours field - show for standard tasks and offset */}
          {(form.watch("type") === "standard" || form.watch("type") === "offset") && (
            <FormField
              control={form.control}
              name="hoursWorked"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {form.watch("type") === "offset" ? t("tasks:form.hoursSettlement") : t("tasks:form.hoursWorked")}
                  </FormLabel>
                  <FormControl>
            <Input
              type="number"
              step="0.5"
              min={form.watch("type") === "offset" ? "-99999" : "0"}
              placeholder={form.watch("type") === "offset" ? "-0.00" : "0.00"}
              {...field}
            />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("tasks:form.status")}</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("tasks:form.statusPlaceholder")}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="pending">{t("tasks:statuses.pending")}</SelectItem>
                    <SelectItem value="in_progress">{t("tasks:statuses.in_progress")}</SelectItem>
                    <SelectItem value="completed">{t("tasks:statuses.completed")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="dueDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("tasks:form.dueDate")}</FormLabel>
                <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP", { locale: sk })
                        ) : (
                          <span>{t("tasks:selectDate")}</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setDueDateOpen(false);
                      }}
                      locale={sk}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="acceptDeadline"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("tasks:form.acceptDeadline")}</FormLabel>
                <Popover open={acceptDeadlineOpen} onOpenChange={setAcceptDeadlineOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP", { locale: sk })
                        ) : (
                          <span>{t("tasks:selectDate")}</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setAcceptDeadlineOpen(false);
                      }}
                      locale={sk}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {showManualAmount ? (
          <FormField
            control={form.control}
            name="manualAmount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("Suma")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    min={taskType === "offset" ? "-99999" : "0"}
                    placeholder="0.00"
                    {...field}
                    value={field.value === null ? "" : field.value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}

        <div className="flex items-center space-x-4">
          <FormField
            control={form.control}
            name="isStarred"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="flex items-center">
                    <Star className="h-4 w-4 mr-1 text-yellow-500" />
                    {t("tasks:form.isStarred")}
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="billable"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="flex items-center">
                    <span className="text-green-600 font-medium">€</span>
                    {t("tasks:form.billable")}
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />
        </div>

        {/* Sekcia položiek úlohy */}
        <TaskItemsForm
          items={items}
          onChange={handleItemsChange}
          taskId={taskId}
          isVatPayer={user?.isVatPayer === true}
          companyVatRate={user?.companyVatRate != null ? Number(user.companyVatRate) : undefined}
        />

        <div className="flex flex-col sm:flex-row justify-between gap-4 bg-muted/30 p-4 rounded-lg">
          <div className="flex-1 space-y-2">
            <h4 className="font-medium text-sm">{t("tasks:form.calculationDetails")}</h4>
            <p className="text-sm text-muted-foreground">{t("tasks:form.client")}: {selectedClient?.name || "-"}</p>
            
            {/* Show different info based on task type */}
            {(taskType === "standard" || taskType === "offset") && (
              <>
                <p className="text-sm text-muted-foreground">{t("tasks:form.hourlyRate")}: {selectedClient ? formatCurrency(parseFloat(selectedClient.hourlyRate) || 0) : "-"}</p>
                <p className="text-sm text-muted-foreground">{t("tasks:form.hoursWorked")}: {form.watch("hoursWorked") || "0"}</p>
                {hoursTotal !== 0 && (
                  <p className="text-sm font-medium text-blue-600">{t("tasks:form.hoursTotal")}: {formatCurrency(hoursTotal)}</p>
                )}
                {taskType === "offset" && form.watch("manualAmount") && parseFloat(String(form.watch("manualAmount") || "0")) !== 0 && (
                  <p className="text-sm text-muted-foreground">{t("tasks:form.manualAmount")}: {formatCurrency(parseFloat(String(form.watch("manualAmount") || "0")))}</p>
                )}
              </>
            )}
            
            {(taskType === "cash" || taskType === "bonus") && (
              <>
                <p className="text-sm text-muted-foreground">{t("tasks:form.hourlyRate")}: {selectedClient ? formatCurrency(parseFloat(selectedClient.hourlyRate) || 0) : "-"}</p>
                <p className="text-sm text-muted-foreground">{t("tasks:form.hoursWorked")}: {form.watch("hoursWorked") || "0"}</p>
                {hoursTotal !== 0 && (
                  <p className="text-sm font-medium text-blue-600">{t("tasks:form.hoursTotal")}: {formatCurrency(hoursTotal)}</p>
                )}
                <p className="text-sm text-muted-foreground">{t("tasks:form.manualAmount")}: {formatCurrency(parseFloat(String(form.watch("manualAmount") || "0")))}</p>
              </>
            )}
            
            {totals.itemsNet !== 0 && (
              <p className="text-sm font-medium text-green-600">
                {isVatPayer && totals.itemsVat > 0 
                  ? `${t("tasks:items.total")}: ${formatCurrency(totals.itemsNet + totals.itemsVat)} (${formatCurrency(totals.itemsNet)} + ${t("common:vat")} ${formatCurrency(totals.itemsVat)})`
                  : `${t("tasks:items.total")}: ${formatCurrency(totals.itemsNet)}`}
              </p>
            )}
          </div>
          <div className="flex-1 text-right">
            <p className="text-3xl font-bold text-primary">{formatCurrency(totalGross)}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {taskType === "standard" ? t("tasks:types.standard") :
               taskType === "cash" ? t("tasks:types.cash") :
               taskType === "offset" ? t("tasks:types.offset") : t("tasks:types.bonus")}
            </p>
            
            {/* VAT breakdown if user is VAT payer */}
            {isVatPayer && totalVat > 0 && (
              <div className="text-xs text-muted-foreground mt-2 space-y-1">
                <p>{t("common:subtotal")}: {formatCurrency(totalNet)}</p>
                <p>{t("common:vat")}: {formatCurrency(totalVat)}</p>
                <p className="font-medium">{t("common:total")}: {formatCurrency(totalGross)}</p>
              </div>
            )}
            
            {/* Standard breakdown for non-VAT payers */}
            {(!isVatPayer || totalVat === 0) && (
              <>
                {taskType === "standard" && (hoursTotal !== 0 && totals.itemsNet !== 0) && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {t("tasks:form.hoursWorked")} + {t("tasks:items.title")}
                  </p>
                )}
                
                {taskType === "offset" && (hoursTotal !== 0 && totals.itemsNet !== 0) && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {t("tasks:form.hoursWorked")} + {t("tasks:items.title")}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-4">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("tasks:actionsButtons.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
